import React from "react";
import { Link } from "react-router-dom";
import { X, Send, Ruler, Maximize, Home, Layers, Building2, Info, ArrowRight, Scale, Ban, FileText } from "lucide-react";
import { assetUrl } from "../../lib/api";
import { STATUS_LABELS, STATUS_COLORS } from "../../lib/constants";

const Stat = ({ icon: Icon, label, value }) => (
  <div className="rounded-lg border border-[#F3EEE3]/12 bg-[#0C1510] px-3 py-2">
    <div className="flex items-center gap-1.5 text-[#C9B79C] mb-0.5">
      <Icon size={12} />
      <span className="eyebrow text-[0.5rem]">{label}</span>
    </div>
    <p className="text-[#F3EEE3] text-[0.82rem] font-medium leading-snug">{value}</p>
  </div>
);

// Static Stage 8 architectural requirements (from the Deer Park Stage 8 Design Guidelines).
const REQUIREMENTS = [
  ["Front / Back setback", "6.0 m / 7.6 m"],
  ["Side setback", "1.2 m"],
  ["Garage", "Double front-attached"],
  ["Min. bungalow / 2-storey", "1,300 / 1,700 sq ft"],
  ["Roof pitch", "5/12 min (6/12 bungalow)"],
];

const LotDetailDialog = ({ open, parcel, builder, stageNumber, inCompare, onToggleCompare, onEnquire, onClose, photo, stageLink, description }) => {
  if (!open || !parcel) return null;
  const p = parcel;
  const reserved = p.status !== "available";
  const color = STATUS_COLORS[p.status] || "#C9B79C";
  const area = p.area_sqft
    ? `${Number(p.area_sqft).toLocaleString()} sq ft${p.area_acres ? ` · ${p.area_acres} ac` : ""}`
    : "—";
  const buildable = p.buildable_sqft ? `${Number(p.buildable_sqft).toLocaleString()} sq ft` : "—";
  const features = (p.features && p.features.length) ? p.features.join(", ") : "Standard";
  const hasReq = stageNumber === 8;

  return (
    <div className="fixed inset-0 z-[100]" data-testid="lot-detail-dialog">
      <div className="fixed inset-0 bg-[#0C1510]/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-md md:max-w-3xl bg-[#12201A] border border-[#F3EEE3]/15 rounded-2xl flex flex-col max-h-[92vh] md:max-h-[86vh] overflow-hidden">
          <button onClick={onClose} className="absolute top-3.5 right-3.5 z-10 text-[#F3EEE3]/60 hover:text-[#F3EEE3]" aria-label="Close" data-testid="lot-detail-close">
            <X size={20} />
          </button>

          {/* Header — fixed */}
          <div className="shrink-0 px-5 pt-5 pb-4 border-b border-[#F3EEE3]/10 pr-12">
            <div className="flex items-center gap-3 flex-wrap">
              <p className="eyebrow text-[#C9B79C]">Lot {p.lot_number}{p.block ? ` · Block ${p.block}` : ""}</p>
              <span
                className="inline-flex items-center gap-1.5 text-[0.62rem] tracking-wide uppercase px-3 py-1 rounded-full text-[#12201A] font-semibold"
                style={{ background: color }}
                data-testid="lot-detail-status"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#12201A]/70" /> {STATUS_LABELS[p.status]}
              </span>
            </div>
            <h3 className="font-display font-light text-2xl text-[#F3EEE3] mt-1.5">Deer Park{stageNumber ? ` · Stage ${stageNumber}` : ""}</h3>
          </div>

          {/* Body — scrollable */}
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {p.builder_name && (
              builder ? (
                <Link
                  to={`/builders/${builder.id}`}
                  data-testid="lot-detail-builder-link"
                  className="group flex items-center gap-3 rounded-xl border border-[#C9B79C]/35 bg-[#C9B79C]/10 p-2.5 hover:bg-[#C9B79C]/20 transition-colors mb-4"
                >
                  {builder.logo_url && (
                    <div className="w-9 h-9 rounded-lg overflow-hidden border border-[#F3EEE3]/15 bg-white shrink-0">
                      <img src={assetUrl(builder.logo_url)} alt={`${builder.name} logo`} className="w-full h-full object-contain" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-[#F3EEE3]/50 text-[0.6rem] uppercase tracking-wide">{reserved ? "Builder" : "Built by"}</p>
                    <p className="text-[#F3EEE3] text-sm font-medium truncate">{builder.name}</p>
                  </div>
                  <span className="inline-flex items-center gap-1 text-[#C9B79C] text-xs whitespace-nowrap group-hover:text-[#F3EEE3]">
                    View homes &amp; models <ArrowRight size={13} className="group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </Link>
              ) : (
                <div className="flex items-center gap-3 rounded-xl border border-[#F3EEE3]/12 bg-[#0C1510] p-2.5 mb-4">
                  <div>
                    <p className="text-[#F3EEE3]/50 text-[0.6rem] uppercase tracking-wide">{reserved ? "Builder" : "Built by"}</p>
                    <p className="text-[#F3EEE3] text-sm font-medium">{p.builder_name}</p>
                  </div>
                </div>
              )
            )}

            <div className={`grid gap-4 ${hasReq ? "md:grid-cols-2 md:items-start" : ""}`}>
              {/* Left column — specs */}
              <div>
                {photo && (
                  <figure className="mb-3">
                    <div className="rounded-xl overflow-hidden border border-[#F3EEE3]/12 aspect-[16/9] bg-[#0C1510]">
                      <img src={assetUrl(photo)} alt={builder ? `${builder.name} home` : "Home example"} className="w-full h-full object-cover" data-testid="lot-detail-photo" />
                    </div>
                    <figcaption className="text-[#F3EEE3]/40 text-[0.62rem] mt-1.5">Example of the builder's work — final design subject to selection.</figcaption>
                  </figure>
                )}
                {description && (
                  <p className="text-[#F3EEE3]/70 text-sm leading-relaxed mb-3" data-testid="lot-detail-description">{description}</p>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <Stat icon={Maximize} label="Lot area" value={area} />
                  <Stat icon={Ruler} label="Frontage" value={p.frontage_m ? `${p.frontage_m} m` : "—"} />
                  <Stat icon={Ruler} label="Depth" value={p.depth_m ? `${p.depth_m} m` : "—"} />
                  <Stat icon={Home} label="Buildable area" value={buildable} />
                  <Stat icon={Layers} label="Walkout" value={p.walkout || "No"} />
                  <Stat icon={Building2} label="Zoning" value={p.zoning || "R1"} />
                </div>
                <div className="mt-2">
                  <Stat icon={Building2} label="Lot type / buildable" value={`${(p.buildable_types && p.buildable_types[0]) || "Single-family, attached garage"}${features !== "Standard" ? ` · ${features}` : ""}`} />
                </div>
              </div>

              {/* Right column — requirements (Stage 8) */}
              {hasReq && (
                <div className="rounded-xl border border-[#F3EEE3]/12 bg-[#0C1510] p-4">
                  <div className="flex items-center gap-2 text-[#C9B79C] mb-2">
                    <Info size={13} />
                    <span className="eyebrow text-[0.55rem]">Building requirements — Stage 8 guidelines</span>
                  </div>
                  <dl className="space-y-1.5">
                    {REQUIREMENTS.map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between gap-3 text-[0.8rem]">
                        <dt className="text-[#F3EEE3]/55">{k}</dt>
                        <dd className="text-[#F3EEE3] font-medium text-right">{v}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
            </div>
          </div>

          {/* Footer — fixed */}
          <div className="shrink-0 border-t border-[#F3EEE3]/10 px-5 py-4">
            {stageLink ? (
              <Link
                to={stageLink}
                onClick={onClose}
                data-testid="lot-detail-view-stage"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#F3EEE3] text-[#12201A] px-6 py-3 rounded-full font-medium hover:bg-white transition-colors"
              >
                View on the stage plan <ArrowRight size={16} />
              </Link>
            ) : reserved ? (
              <div className="flex flex-col md:flex-row gap-2 md:items-center">
                <div
                  data-testid="lot-detail-soldout"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold uppercase tracking-wide text-sm cursor-default select-none"
                  style={{ background: color + "22", color: "#F3EEE3", border: `1px solid ${color}` }}
                >
                  {p.status === "sold"
                    ? <><Home size={16} style={{ color }} /> Home coming</>
                    : <><Ban size={16} style={{ color }} /> Reserved</>}
                </div>
                {p.status === "sold" && (
                  builder?.website ? (
                    <a
                      href={builder.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="lot-detail-buy-home"
                      className="flex-1 inline-flex items-center justify-center gap-2 bg-[#F3EEE3] text-[#12201A] px-6 py-3 rounded-full font-medium hover:bg-white transition-colors"
                    >
                      <Home size={16} /> Buy a home here
                    </a>
                  ) : builder ? (
                    <Link
                      to={`/builders/${builder.id}`}
                      data-testid="lot-detail-buy-home"
                      className="flex-1 inline-flex items-center justify-center gap-2 bg-[#F3EEE3] text-[#12201A] px-6 py-3 rounded-full font-medium hover:bg-white transition-colors"
                    >
                      <Home size={16} /> Buy a home here
                    </Link>
                  ) : null
                )}
              </div>
            ) : (
              <div className="flex flex-col md:flex-row gap-2 md:items-center">
                <button
                  onClick={() => onEnquire(p)}
                  data-testid="lot-detail-enquire"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-[#F3EEE3] text-[#12201A] px-6 py-3 rounded-full font-medium hover:bg-white transition-colors"
                >
                  <Send size={16} /> Enquire about this lot
                </button>
                {onToggleCompare && (
                  <button
                    onClick={() => onToggleCompare(p)}
                    data-testid="lot-detail-compare"
                    className={`inline-flex items-center justify-center gap-2 px-6 py-2.5 md:py-3 rounded-full text-sm border transition-colors ${inCompare ? "border-[#C9B79C] bg-[#C9B79C]/15 text-[#F3EEE3]" : "border-[#F3EEE3]/25 text-[#F3EEE3]/80 hover:border-[#C9B79C]/60"}`}
                  >
                    <Scale size={15} /> {inCompare ? "Added — remove" : "Add to compare"}
                  </button>
                )}
              </div>
            )}
            {p.plan_pdf_url && (
              <a
                href={assetUrl(p.plan_pdf_url)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="lot-detail-plot-plan"
                className="mt-2 w-full inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full text-sm border border-[#C9B79C]/45 text-[#F3EEE3]/85 hover:bg-[#C9B79C]/15 hover:text-[#F3EEE3] transition-colors"
              >
                <FileText size={15} /> View plot plan (PDF)
              </a>
            )}
            <p className="text-center text-[#F3EEE3]/40 text-[0.7rem] mt-2.5">Full plans, permits &amp; geotechnical reports are in the documents section below.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LotDetailDialog;
